// Rank-color styling for the drill picker. All curriculum-specific values
// (which color maps to which belt level, how levels group into stages, sort
// order, per-drill overrides) come from the school's `drillPickerConfig`
// (Firestore). This module intentionally has no hardcoded per-school styling
// so a new school can be onboarded via data only — see ARCHITECTURE.md.
const FALLBACK_NEUTRAL_STYLE = 'bg-stone-50 border-stone-200 hover:bg-stone-100';

function levelKey(level) {
  return String(level);
}

function getTrackPickerConfig(config, track = 'adult') {
  return config?.[track] ?? null;
}

function getNeutralStyle(config) {
  return config?.neutralStyle ?? FALLBACK_NEUTRAL_STYLE;
}

function resolveBeltStyle(styleValue, config) {
  if (styleValue === 'neutral') {
    return getNeutralStyle(config);
  }
  return styleValue ?? getNeutralStyle(config);
}

export function getLowestBeltLevelForDrill(drillId, belts) {
  const matchingBelts = belts.filter((belt) => belt.drillIds.includes(drillId));
  if (matchingBelts.length === 0) return null;
  return matchingBelts.reduce((min, belt) => (belt.level < min.level ? belt : min)).level;
}

export function getDrillColorLevel(drill, belts, track = 'adult', config) {
  if (track === 'youth') {
    return getLowestBeltLevelForDrill(drill.id, belts);
  }

  const trackConfig = getTrackPickerConfig(config, track);
  const overrides = trackConfig?.colorOverrides ?? {};
  if (drill.id in overrides) {
    return overrides[drill.id];
  }

  return getLowestBeltLevelForDrill(drill.id, belts);
}

export function getColorLevelSortIndex(level, track = 'adult', config) {
  if (level == null) return 0;
  const trackConfig = getTrackPickerConfig(config, track);
  const order = trackConfig?.sortOrder;
  if (order && levelKey(level) in order) {
    return order[levelKey(level)];
  }
  // No configured sort order — fall back to sorting by the raw level number.
  return level;
}

export function getDrillStage(colorLevel, track = 'adult', config) {
  if (colorLevel == null) return null;
  const trackConfig = getTrackPickerConfig(config, track);
  const mapping = trackConfig?.stageByColorLevel;
  if (!mapping) return null;
  return mapping[levelKey(colorLevel)] ?? null;
}

export function getStagesForTrack(track = 'adult', config) {
  const trackConfig = getTrackPickerConfig(config, track);
  return trackConfig?.stages ?? [];
}

export function getBeltRowClasses(
  level,
  { aboveBelt = false, selected = false, dragging = false, track = 'adult', config } = {}
) {
  if (dragging) {
    return 'opacity-50 cursor-grabbing border-primary-600 bg-primary-100 border-2';
  }

  if (selected) {
    return 'border-primary-500 bg-primary-50';
  }

  const trackConfig = getTrackPickerConfig(config, track);
  const styles = trackConfig?.beltLevelStyles ?? {};
  const fallback = getNeutralStyle(config);
  const rankStyles = resolveBeltStyle(styles[levelKey(level)], config) ?? fallback;

  if (aboveBelt) {
    return `${rankStyles} border-orange-400`;
  }

  return rankStyles;
}
